import {
  TpDataObject,
  TpDataObjectInput,
  TpETLObjectTypes,
  TpETLInputTableColumn,
  isTpETLObjectType,
  TpETLTableColumn,
  TpDataObjectTable,
  TpDataObjectMatrix,
} from '../../etl-data-types/interfaces';
import {
  TpETLComponentTemplateDataObject,
  TpETLComponentTemplateOutputObject,
  TpETLComponenTypeTemplateTableColumn,
} from '../../../../api/models/etl';

/**
 * Convert a docker template input/output defintion to a manifest input/output definition.
 */
export function mapTemplateObjectToManifestDataObject(
  templateDataType: TpETLComponentTemplateDataObject,
): TpDataObjectInput {
  const dataObjectType = templateDataType.dataType;
  if (!isTpETLObjectType(dataObjectType)) {
    throw new Error(`Unsupported data object type: ${dataObjectType}`);
  }
  switch (dataObjectType) {
    case TpETLObjectTypes.TABLE:
      return {
        type: TpETLObjectTypes.TABLE,
        columns: [] as TpETLInputTableColumn[],
      };
    case TpETLObjectTypes.TEXT:
      return {
        type: TpETLObjectTypes.TEXT,
      };
    case TpETLObjectTypes.MATRIX:
      return {
        type: TpETLObjectTypes.MATRIX,
        obsMetadataColumns: [] as TpETLInputTableColumn[],
        featureMetadataColumns: [] as TpETLInputTableColumn[],
        linkColumnId: templateDataType.linkColumnId,
      };
    default:
      throw dataObjectType satisfies never; // This should never happen due to the type guard
  }
}

function mapTemplateColumnToManifestColumn(col: TpETLComponenTypeTemplateTableColumn): TpETLTableColumn {
  return {
    identifier: col.id,
    name: col.name,
    dataType: col.dataType,
    description: col.description,
  };
}

/**
 * Convert a docker template output definition to a data object used for the dry run.
 */
export function createDataObjectFromOutputDefinition(
  outputDefinition: TpETLComponentTemplateOutputObject,
): TpDataObject {
  const dataObjectType = outputDefinition.dataType;
  if (!isTpETLObjectType(dataObjectType)) {
    throw new Error(`Unsupported data object type: ${dataObjectType}`);
  }
  switch (dataObjectType) {
    case TpETLObjectTypes.TABLE: {
      const templateColumns = outputDefinition.columns;
      return {
        type: TpETLObjectTypes.TABLE,
        columns: templateColumns.map(mapTemplateColumnToManifestColumn),
      };
    }
    case TpETLObjectTypes.TEXT:
      return {
        type: TpETLObjectTypes.TEXT,
      };
    case TpETLObjectTypes.MATRIX:
      return {
        type: TpETLObjectTypes.MATRIX,
        obsMetadataColumns: outputDefinition.observationMetadataColumns.map(mapTemplateColumnToManifestColumn),
        featureMetadataColumns: outputDefinition.featureMetadataColumns.map(mapTemplateColumnToManifestColumn),
        linkColumnId: outputDefinition.linkColumnId,
      };
    default:
      throw dataObjectType satisfies never; // This should never happen due to the type guard
  }
}

function addTableColumns(baseColumns: TpETLTableColumn[], extraColumns: TpETLTableColumn[][]): TpETLTableColumn[] {
  const columnsMap = new Map<string, TpETLTableColumn>();
  for (const col of baseColumns) {
    columnsMap.set(col.identifier, col);
  }
  for (const extraCols of extraColumns) {
    for (const col of extraCols) {
      if (!columnsMap.has(col.identifier)) {
        columnsMap.set(col.identifier, structuredClone(col));
      }
    }
  }
  // Works because Map preserves insertion order
  return Array.from(columnsMap.values());
}

/**
 * Given a data object used for a dry run, update its properties based on the input objects.
 * This is used to transfer metadata from input objects to the output object
 * (e.g. input table columns to output table columns).
 */
export function updateDataObjectFromInputObjects(
  dataObject: TpDataObject,
  inputObjects: TpDataObject[],
  transferType: 'union' | 'intersection' = 'union',
): void {
  if (transferType !== 'union') {
    throw new Error(`Intersection transfer type is not supported yet: ${transferType}`);
  }
  const dataObjectType = dataObject.type;

  // Filter input objects to only those that match the data object type
  const filteredInputObjects = inputObjects.filter((inputObject) => inputObject.type === dataObjectType);
  if (filteredInputObjects.length < inputObjects.length) {
    console.warn(`Transfer only supports same-type data objects: ${dataObject.type}`);
  }

  switch (dataObjectType) {
    case TpETLObjectTypes.TEXT:
      return; // Text objects do not have extra properties to transfer
    case TpETLObjectTypes.TABLE: {
      const tableInputs: TpDataObjectTable[] = filteredInputObjects as TpDataObjectTable[];
      dataObject.columns = addTableColumns(
        dataObject.columns,
        tableInputs.map((inputObject) => inputObject.columns),
      );
      return;
    }
    case TpETLObjectTypes.MATRIX: {
      const matrixInputs: TpDataObjectMatrix[] = filteredInputObjects as TpDataObjectMatrix[];
      dataObject.obsMetadataColumns = addTableColumns(
        dataObject.obsMetadataColumns,
        matrixInputs.map((inputObject) => inputObject.obsMetadataColumns),
      );
      dataObject.featureMetadataColumns = addTableColumns(
        dataObject.featureMetadataColumns,
        matrixInputs.map((inputObject) => inputObject.featureMetadataColumns),
      );
      if (dataObject.linkColumnId === '' && matrixInputs.length > 0) {
        // If the link column is not set, use the first input's link column
        dataObject.linkColumnId = matrixInputs[0].linkColumnId;
      }
      return;
    }
    default:
      throw dataObjectType satisfies never; // This should never happen due to the type guard
  }
}
